/**
 * Bounded ring buffer of `csmClientMessageId`s the help-seeker tab has
 * recently consumed via the optimistic-add path. Used by the realtime
 * `messageAdded` listener (`live-chat-manager.tsx`) to drop the matching
 * echo so the bubble does not appear twice.
 *
 * The buffer is intentionally small — Twilio realtime echoes typically
 * arrive within a second or two of the send, so a 32-entry FIFO is plenty
 * even for rapid-fire sends. Stored at module scope so it survives
 * component remounts within a single tab.
 */
const RECENT_ID_LIMIT = 32;
const recentClientMessageIds: string[] = [];

/**
 * Records that the help-seeker tab has just optimistically added a message
 * with this client id; subsequent `messageAdded` echoes carrying the same
 * id should be discarded by `consumeRecentCsmClientMessageId`.
 */
export const recordRecentCsmClientMessageId = (id: string): void => {
	if (!id) return;
	recentClientMessageIds.push(id);
	if (recentClientMessageIds.length > RECENT_ID_LIMIT) {
		recentClientMessageIds.shift();
	}
};

/**
 * If `id` is in the recent buffer, removes it and returns true (caller
 * should treat this as "already-handled, drop the echo"). Otherwise
 * returns false (caller should proceed to add the message normally).
 */
export const consumeRecentCsmClientMessageId = (id: string): boolean => {
	if (!id) return false;
	const idx = recentClientMessageIds.indexOf(id);
	if (idx === -1) return false;
	recentClientMessageIds.splice(idx, 1);
	return true;
};

/**
 * Test-only: clears the recent buffer between tests so module state
 * does not leak across test cases.
 */
export const __resetRecentCsmClientMessageIdsForTest = (): void => {
	recentClientMessageIds.length = 0;
};
